/**
 * Sentry error tracking client implementation (browser/web).
 *
 * Wraps @sentry/react (or @sentry/browser) with the IErrorClient
 * interface. Applications import this, never Sentry directly.
 *
 * See Part 22 §120.1 of the Tech Guidelines.
 */

import type {
  IErrorClient,
  ErrorTrackingInitConfig,
  ErrorUser,
  ErrorScope,
  ErrorCaptureContext,
  MessageCaptureContext,
} from './error-client.types';
import { sanitizeSentryExtra } from '../privacy/sanitize';
import { lazyRequire } from '../utils/lazy-require';
import { assertObservabilityEnvironment } from '../environment/observability-environment';

/**
 * Initializes Sentry for browser environments and returns an IErrorClient.
 *
 * Call once at app bootstrap (main.tsx), before React renders.
 * Returns a client that can be passed to <ObservabilityProvider>.
 *
 * Note: The actual Sentry SDK is lazy-loaded. Consumers must have
 * `@sentry/react` installed as a dependency.
 */
export function initErrorTracking(config: ErrorTrackingInitConfig): IErrorClient {
  let sentry: SentryLike | null;

  // A DSN plus an unknown tier is a misconfiguration, not a runtime hiccup:
  // it mislabels every event on a tier nobody can tell apart afterwards
  // (W0.3 · F16). Refuse LOUDLY, and deliberately OUTSIDE the try/catch
  // below — inside, the throw would be swallowed into a console line and a
  // silent no-op client.
  if (config.dsn) {
    assertObservabilityEnvironment(config.environment, 'initErrorTracking');
  }

  try {
    // 1. Prefer pre-loaded SDK (required for Vite/ESM browser environments)
    if (config.sdk) {
      sentry = config.sdk as SentryLike;
    } else {
      // 2. Fall back to require() (works in Node.js / CommonJS)
      sentry = lazyRequire<SentryLike>('@sentry/react');
    }

    if (sentry && config.dsn) {
      sentry.init({
        dsn: config.dsn,
        environment: config.environment,
        release: config.release,
        tracesSampleRate: config.tracesSampleRate ?? 0.1,
        replaysSessionSampleRate: config.replaysSessionSampleRate ?? 0.1,
        replaysOnErrorSampleRate: config.replaysOnErrorSampleRate ?? 1.0,
        beforeSend: config.beforeSend as SentryLike['init'] extends (c: infer C) => void
          ? C extends { beforeSend?: infer B }
            ? B
            : never
          : never,
      });
    } else {
      sentry = null;
    }
  } catch (err) {
    console.error('[Observability] Sentry init failed:', err);
    sentry = null;
  }

  return createErrorClient(sentry);
}

/**
 * Creates the IErrorClient from an initialized Sentry instance.
 */
function createErrorClient(sentry: SentryLike | null): IErrorClient {
  return {
    captureException(error: Error | unknown, context?: ErrorCaptureContext): string | undefined {
      if (!sentry) return undefined;

      const extra = context?.extra ? sanitizeSentryExtra(context.extra) : undefined;

      return sentry.captureException(error, {
        level: context?.level,
        tags: context?.tags,
        extra,
      });
    },

    captureMessage(message: string, context?: MessageCaptureContext): string | undefined {
      if (!sentry) return undefined;

      const extra = context?.extra ? sanitizeSentryExtra(context.extra) : undefined;

      return sentry.captureMessage(message, {
        level: context?.level,
        tags: context?.tags,
        extra,
      });
    },

    setUser(user: ErrorUser): void {
      sentry?.setUser({
        id: user.id,
        ...(user.email ? { email: user.email } : {}),
      });
    },

    clearUser(): void {
      sentry?.setUser(null);
    },

    setTag(key: string, value: string): void {
      sentry?.setTag(key, value);
    },

    setTags(tags: Record<string, string>): void {
      sentry?.setTags(tags);
    },

    configureScope(callback: (scope: ErrorScope) => void): void {
      if (!sentry) return;
      // Sentry v8+ removed configureScope(); mutate the active scope via
      // getCurrentScope(). Legacy fallback for older SDKs (BL-026).
      if (typeof sentry.getCurrentScope === 'function') {
        callback(sentry.getCurrentScope() as unknown as ErrorScope);
        return;
      }
      sentry.configureScope?.(callback as (scope: unknown) => void);
    },

    withScope<T>(callback: (scope: ErrorScope) => T): T {
      if (!sentry) {
        const noopScope: ErrorScope = {
          setTag: () => {
            /* no-op */
          },
          setContext: () => {
            /* no-op */
          },
          setExtra: () => {
            /* no-op */
          },
          setUser: () => {
            /* no-op */
          },
          setLevel: () => {
            /* no-op */
          },
        };
        return callback(noopScope);
      }

      let result: T;
      sentry.withScope((scope: unknown) => {
        result = callback(scope as ErrorScope);
      });
      return result!;
    },
  };
}

// ── Sentry minimal type (avoids hard dependency on @sentry/* types) ──

interface SentryLike {
  init(config: Record<string, unknown>): void;
  captureException(error: unknown, context?: Record<string, unknown>): string;
  captureMessage(message: string, context?: Record<string, unknown>): string;
  setUser(user: Record<string, unknown> | null): void;
  setTag(key: string, value: string): void;
  setTags(tags: Record<string, string>): void;
  /** v8+ returns the active scope; preferred over the removed configureScope(). */
  getCurrentScope?(): unknown;
  /** Legacy v7 API — removed in v8+, kept optional for backwards compat. */
  configureScope?(callback: (scope: unknown) => void): void;
  withScope(callback: (scope: unknown) => void): void;
}
